<template>
  <div class="app-page">
    <div class="appliction-page-success" v-if="successMsgPage != null">
      <a-result status="success" :title="$t('user.Reopen Application')" :sub-title="successMsgPage">
        <template #icon>
          <svg-icon class="msg-icon" icon-class="success"></svg-icon>
        </template>
        <template #extra>
          <div class="btn-group-success">
            <a-button class="btn-view" @click="successMsgPage = null">
              {{ $t('user.View Group List') }}
            </a-button>
            <router-link :to="{ path: '/home' }">
              <a-button type="primary" class="btn-back">
                {{ $t('user.Back to Home') }}
              </a-button>
            </router-link>
          </div>
        </template>
      </a-result>
    </div>
    <template v-else>
      <div class="application-page-header">
        <div class="page-title">{{ $t('user.' + $route.meta?.title) }}</div>
        <div class="application-date" v-if="isOfflineMode">
             <br/>
            {{ $t('user.This Session is Offline') }}
        </div>
        <div class="application-date" v-else>
          <div class="application-date-item">
            <CalendarOutlined />
            {{ $t('user.ROAR Application HRBP Review Due Date') }}:
            {{ hrbpReviewDate[1] }}
            <a-tooltip>
              <template #title>
                {{ $t('user.Please review the status of application, remind leaders for pending cases and reopen assessment if needed before the due date. System will automatically publish application status to the employee after this due date.') }}
              </template>
              <QuestionCircleFilled />
            </a-tooltip>
          </div>
          <div class="application-date-item">
            <CalendarOutlined />
            {{ $t('user.Panel Result Submission Due Date') }}:
            {{ hrbpSubmitDate[1] }}
            <a-tooltip>
              <template #title>
                {{ $t('user.After getting global confirmation, please upload the panel result and comments before the due date. TMOD will publish the result to all before payroll cut-off date.') }}
              </template>
              <QuestionCircleFilled />
            </a-tooltip>
          </div>
        </div>
      </div>
      <div class="application-page-container">
        <section>
          <div class="btn-action-group">
            <a-upload v-if="!isOfflineMode" :disabled="!canUpload" class="btn-upload" :action="uploadDataUrl" :headers="{ Authorization }" :showUploadList="false" :beforeUpload="beforeUpload" @change="handleUploadDataChange">
              <a-button :disabled="!canUpload">
                <upload-outlined></upload-outlined>
                {{ $t('user.Upload') }}
              </a-button>
            </a-upload>
            <a-button class="btn-export" @click="exportData">
              <template #icon>
                <SaveOutlined />
              </template>
              {{ $t('user.Export to Excel') }}
            </a-button>
          </div>
          <a-input-search clss="input-search-panel" v-model:value="keyword" allow-clear :placeholder="$t('user.Employee / Review Manager')" :loading="loading" @search="onSearch" />
        </section>
        <section class="application-counter">
          <div>
            <div class="counter-text">
              <span>{{ $t('user.Number of Total Talents') }}: {{ talentsTotal }}</span>
              <span>{{ $t('user.Number of Applicants') }}: {{ applicationTotal }}</span>
            </div>
            <div>
              <a-button class="btn-download-assessment" @click="exportAssessment" :disabled="selectedRowKeys.length == 0">
                {{ $t('user.Download assessment') }}
              </a-button>
              <a-button class="btn-export" @click="exportEmployeePromotionRecords" :disabled="selectedRowKeys.length == 0">
                {{ $t('user.Download employee application records') }}
              </a-button>
              <span>
                {{ $t('user.Selected') }} {{ selectedRowKeys.length }}
                {{ $t('user.items') }}
              </span>
            </div>
          </div>
          <div class="page-pagination">
            <a-pagination @change="fetchData" v-model:current="pagination.current" :total="pagination.total" v-model:pageSize="pagination.pageSize" :pageSizeOptions="pagination.pageSizeOptions" size="small" show-less-items show-size-changer show-quick-jumper />
          </div>
        </section>
        <section></section>
        <a-table
          class="job-table"
          :rowKey="(record) => record.employee_id"
          :columns="columns"
          :data-source="applicationRecord"
          @change="onChange"
          :pagination="false"
          :row-selection="{
            selectedRowKeys,
            onChange: onSelectChange,
            getCheckboxProps,
          }"
        >
          <template v-slot:emptyText>
            <Empty />
          </template>
          <template #customFilterDropdown="{ confirm, setSelectedKeys, selectedKeys, column, clearFilters }">
            <div v-if="column.key == 'status'">
              <RoarFilter :options="statusOptions" :confirm="confirm" :setSelectedKeys="setSelectedKeys" :selectedKeys="selectedKeys" :clearFilters="clearFilters" />
            </div>
            <div v-if="column.key == 'current_brand'">
              <RoarFilter :options="this.brands.map((item) => ({ text: item, value: item }))" :confirm="confirm" :setSelectedKeys="setSelectedKeys" :selectedKeys="selectedKeys" :clearFilters="clearFilters" />
            </div>
          </template>
          <template v-slot:headerCell="{ title, _ }">
            {{ $t('user.' + title) }}
          </template>

          <template v-slot:bodyCell="{ record, column }">
            <template v-if="column.key === 'name'">
              <span :class="{ highlight: record.name == keyword.trim() }">
                {{ record.name }}
              </span>
            </template>
            <template v-if="column.key === 'lm_name'">
              <span :class="{ highlight: record.lm_name == keyword.trim() }">
                {{ record.lm_name }}
              </span>
            </template>
            <template v-if="column.key === 'status'">
               <ApplyStatusColumn :data="record" />
            </template>
            <template v-else-if="column.key === 'action'">
              <template v-if="record.status != 0">
                <router-link :to="{ path: `/group/detail/${record.id}` }" v-if="record.status != 10">
                  <a-button type="link">{{ $t('user.View') }}</a-button>
                </router-link>
                <a-button v-else type="link" disabled>
                  {{ $t('user.View') }}
                </a-button>
                <br />
                <template v-if="!isOfflineMode">
                  <a-button type="link" :disabled="(record.status != 2 && record.status != -1) || canUpload || !canReopen" @click="reopenManager(record)">
                    <!-- {{ record.status != 2 && record.status != -1 }} | {{ canUpload }} | {{ canReopen }} -->
                    {{ $t('user.Reopen Review Manager') }}
                  </a-button>
                  <br />
                  <a-button type="link" :disabled="(record.status != 2 && record.status != -1 && record.status != 1) || canUpload || !canReopen" @click="reopenBoth(record)">
                    {{ $t('user.Reopen Both') }}
                  </a-button>
                </template>
              </template>
            </template>
          </template>
        </a-table>
        <div class="table-pagination">
          <div></div>
          <a-pagination @change="fetchData" v-model:current="pagination.current" :total="pagination.total" v-model:pageSize="pagination.pageSize" :pageSizeOptions="pagination.pageSizeOptions" size="small" show-less-items show-size-changer show-quick-jumper />
        </div>
      </div>
    </template>
    <a-modal v-model:visible="downloadAssessmentVisible" centered :title="$t('user.Download assessment')" :maskClosable="false" width="480px" :footer="null">
      <p>
        {{ $t('user.Please click to choose which type of assessment you want. Once the file is ready, a download link will be sent to your Publicis mailbox') }}
      </p>
      <div class="download-assessment-buttons">
        <a-button class="download-assessment-button" @click="downloadLatestAssessment">
          {{ $t('user.Download latest assessment') }}
        </a-button>
        <a-button class="download-assessment-button" @click="downloadAllAssessment">
          {{ $t('user.Download all assessment') }}
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script>
  import { getGroupApplicationList, reOpenApplication, reSetApplication, hrbpExport, hrbpAssessExport, promotionRecordsExport } from '@/api/index'
  import { getBrandList } from '@/api/admin'
  import { mapGetters } from 'vuex'
  import { Modal, message } from 'ant-design-vue'
  import { SaveOutlined, UploadOutlined, CalendarOutlined, QuestionCircleFilled } from '@ant-design/icons-vue'
  import { ApplyStatusColumn } from '@/components/ApplyStatus/index.js'
  import RoarFilter from '@/components/RoarFilter'
  import Empty from '@/components/Empty'
  import bus from '@/lib/bus'

  export default {
    name: 'Index',
    components: {
      ApplyStatusColumn,
      UploadOutlined,
      CalendarOutlined,
      SaveOutlined,
      QuestionCircleFilled,
      RoarFilter,
      Empty,
    },
    data() {
      return {
        talentsTotal: 0,
        applicationTotal: 0,
        loading: false,
        uploadDataUrl: '/api/RoleC/Import',

        keyword: '',
        filteredInfo: {},
        pagination: {
          total: 0,
          current: 1,
          pageSize: 10,
          pageSizeOptions: ['10', '30', '50'],
        },
        successMsgPage: null,
        applicationRecord: [],
        selectedRowKeys: [],
        allSelectedRowKeys: [],
        brands: [],
        statusOptions: [
          {
            text: this.$t('user.Pending'),
            value: 'pd',
          },
          {
            text: this.$t('user.Approve for Panel'),
            value: 'ap',
          },
          {
            text: this.$t('user.Reopen'),
            value: 'ro',
          },
          {
            text: this.$t('user.Disapprove for Panel'),
            value: 'dp',
          },
          {
            text: this.$t('user.No Application'),
            value: 'na',
          },
          {
            text: this.$t('user.Promotion'),
            value: 'r1',
          },
          {
            text: this.$t('user.Salary Adjustment'),
            value: 'r2',
          },
          {
            text: this.$t('user.Transfer'),
            value: 'r3',
          },
          {
            text: this.$t('user.Try Again Next Time'),
            value: 'r4',
          },
          {
            text: this.$t('user.No show'),
            value: 'r5',
          },
          {
            text: this.$t('user.No Participation_Not Win'),
            value: 'r6',
          },
          {
            text: this.$t('user.Resigned'),
            value: 'r7',
          }
        ],
        downloadAssessmentVisible: false,
      }
    },
    computed: {
      columns() {
        const filtered = this.filteredInfo || {}
        const statusColumn = {
          title: 'Status',
          key: 'status',
          dataIndex: 'status',
          width: 190,
          customFilterDropdown: true,
        }
        const currentBrandColumn = {
          title: 'Current Brand',
          key: 'current_brand',
          dataIndex: 'current_brand',
          width: 190,
          customFilterDropdown: true,
        }
        if (this.overSubmissionDate()) {
          statusColumn.filters = [
            {
              text: 'All',
              value: 'all',
            },
            {
              text: `Panel Result: Promotion`,
              value: 'r1',
            },
            {
              text: `Panel Result: Salary Adjustment`,
              value: 'r2',
            },
            {
              text: 'Panel Result:Transfer',
              value: 'r3',
            },
            {
              text: 'Panel Result:Try Again Next Time',
              value: 'r4',
            },
            // {
            //   text: 'Disapprove for Panel',
            //   value: 'dp',
            // },
            {
              text: 'No Application',
              value: 'na',
            },
          ]
        }
        return [
          {
            title: 'Name',
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: 'Current Role',
            key: 'current',
            dataIndex: 'current',
          },
          {
            title: 'Target Role',
            key: 'target',
            dataIndex: 'target',
          },
          {
            title: 'Review Manager',
            key: 'lm_name',
            dataIndex: 'lm_name',
            width: 150,
          },
          currentBrandColumn,
          statusColumn,
          {
            title: 'Action',
            key: 'action',
            dataIndex: 'action',
            width: 150,
          },
        ]
      },
      ...mapGetters({
        hrbpReviewDate: 'user/hrbpReviewDate',
        hrbpSubmitDate: 'user/hrbpSubmitDate',
        Authorization: 'user/accessToken',
        ability: 'acl/ability',
        offlineMode: 'user/offlineMode',
      }),
      isOfflineMode() {
        return this.offlineMode == 1
      },
      canUpload() {
        const ability = this.ability.find((item) => item.key === 'can_upload')
        return ability.value
      },
      canReopen() {
        const ability = this.ability.find((item) => item.key === 'hrbp_reviewing')
        return ability.value
      },
      appliedApplicationCount() {
        return this.applicationRecord.filter((item) => {
          return item.status !== 0
        }).length
      },
    },
    async mounted() {
      this.title = this.$route.meta?.title

      this.fetchData().then((res) => {
        this.applicationTotal = res.info.NumberOfApplicants
        this.talentsTotal = res.info.NumberOfTotalTalents
      })

      const res = await getBrandList()
      this.brands = res.data
    },
    methods: {
      getCheckboxProps: (record) => ({
        disabled: record.employee_id === 0,
      }),
      overSubmissionDate() {
        return this.canUpload
        // return (
        //   // new Date(this.hrbpSubmitDate[1]).getTime() - new Date().getTime() < 0
        // )
      },
      beforeUpload(file) {
        const isExcel = file.type === 'application/vnd.ms-excel' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        if (!isExcel) {
          message.error(this.$t('admin.Incorrect Excel template type.'))
        }
        return isExcel
      },
      handleUploadDataChange({ file }) {
        if (file.status !== 'uploading') {
          console.log(file)
        }
        if (file.status === 'done') {
          if (file.response.code == 200) {
            message.success(`${file.name} file uploaded successfully`)
            this.fetchData()
          } else {
            message.error(file.response.msg)
          }
        } else if (file.status === 'error') {
          message.error(`${file.name} file upload failed.`)
        }
      },
      exportData() {
        hrbpExport(this.Authorization).then((msg) => {
          const fileDesc = msg.headers['content-disposition']
          const fileName = fileDesc.substr(fileDesc.indexOf('filename') + 10)
          const file = new Blob([msg.data])
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
            var link = document.createElement('a')
            link.style.display = 'none'
            link.href = e.target.result
            link.download = fileName

            var body = document.querySelector('body')
            body.appendChild(link)
            link.click()
            body.removeChild(link)
            window.URL.revokeObjectURL(link.href)
          }
        })
      },
      fetchData() {
        const params = {
          pageSize: this.pagination.pageSize || 10,
          pageNum: this.pagination.current || 1,
          keywords: this.keyword,
          pagination: this.pagination,
          filters: {
            status: this.filteredInfo.status,
            current_brand: this.filteredInfo.current_brand,
          },
        }
        this.loading = true
        return getGroupApplicationList(params)
          .then((res) => {
            if (res.code == 200) {
              this.pagination.total = res.page.total || 0
              this.applicationRecord = res.data
            }
            return res
          })
          .finally(() => {
            this.loading = false
          })
      },
      onSearch() {
        this.pagination.current = 1
        return this.fetchData()
      },

      onChange(pagination, filters, sorter) {
        // console.log('params', pagination, filters, sorter)
        this.filteredInfo = filters
        return this.fetchData()
      },

      reopenManager(data) {
        const vm = this
        Modal.confirm({
          title: vm.$t('user.Reopen'),
          okText: vm.$t('user.Yes'),
          cancelText: vm.$t('user.No'),
          closable: true,
          content: vm.$t('user.Are you sure you want to reopen review manager’s assessment?'),
          onOk() {
            return reOpenApplication(data.id).then(
              (res) => {
                if (res.code === 200) {
                  vm.successMsgPage = vm.$t('admin.Review Manager can now re-assess.')
                  // data.status = 10
                  data.status = 1
                } else {
                  message.info(res.msg)
                }
              },
              (err) => {
                message.info(err.msg || 'The system is busy. Try again later.')
              }
            )
          },
        })
      },
      reopenBoth(data) {
        const vm = this
        Modal.confirm({
          title: vm.$t('user.Reopen'),
          okText: vm.$t('user.Yes'),
          cancelText: vm.$t('user.No'),
          closable: true,
          content: vm.$t('user.Are you sure you want to reopen review manager’s assessment and employee’s application?'),
          onOk() {
            return reSetApplication(data.id).then(
              (res) => {
                if (res.code === 200) {
                  vm.successMsgPage = vm.$t('admin.Review manager and employee can now re-assess.')
                  data.status = 10
                } else {
                  message.info(res.msg)
                }
              },
              (err) => {
                message.info(err.msg || 'The system is busy. Try again later.')
              }
            )
          },
        })
      },
      onSelectChange(selectedRowKeys) {
        this.allSelectedRowKeys[this.pagination.current] = selectedRowKeys
        this.selectedRowKeys = this.allSelectedRowKeys.filter((item) => item != null).flatMap((item) => item)
      },
      exportAssessment() {
        this.downloadAssessmentVisible = true
      },
      exportEmployeePromotionRecords() {
        promotionRecordsExport(this.Authorization, this.selectedRowKeys).then((msg) => {
          const fileDesc = msg.headers['content-disposition']
          const fileName = fileDesc.substr(fileDesc.indexOf('filename') + 10)
          const file = new Blob([msg.data])
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = (e) => {
            var link = document.createElement('a')
            link.style.display = 'none'
            link.href = e.target.result
            link.download = fileName

            var body = document.querySelector('body')
            body.appendChild(link)
            link.click()
            body.removeChild(link)
            window.URL.revokeObjectURL(link.href)
          }
        })
      },
      downloadLatestAssessment() {
        this.downloadAssessmentVisible = false
        hrbpAssessExport(this.Authorization, this.selectedRowKeys, true).then((msg) => {
          message.success(this.$t('user.Your request has been received'))
          setTimeout(() => {
            bus.emit('fetchMsg')
          }, Math.floor(Math.random() * 3 + 3) * 1000)
          // const fileDesc = msg.headers['content-disposition']
          // const fileName = fileDesc.substr(fileDesc.indexOf('filename') + 10)
          // const file = new Blob([msg.data])
          // const reader = new FileReader()
          // reader.readAsDataURL(file)
          // reader.onload = (e) => {
          //   var link = document.createElement('a')
          //   link.style.display = 'none'
          //   link.href = e.target.result
          //   link.download = fileName
          //   var body = document.querySelector('body')
          //   body.appendChild(link)
          //   link.click()
          //   body.removeChild(link)
          //   window.URL.revokeObjectURL(link.href)
          // }
        })
      },
      downloadAllAssessment() {
        this.downloadAssessmentVisible = false
        hrbpAssessExport(this.Authorization, this.selectedRowKeys).then((msg) => {
          message.success(this.$t('user.Your request has been received'))
          setTimeout(() => {
            bus.emit('fetchMsg')
          }, Math.floor(Math.random() * 3 + 3) * 1000)
          // const fileDesc = msg.headers['content-disposition']
          // const fileName = fileDesc.substr(fileDesc.indexOf('filename') + 10)
          // const file = new Blob([msg.data])
          // const reader = new FileReader()
          // reader.readAsDataURL(file)
          // reader.onload = (e) => {
          //   var link = document.createElement('a')
          //   link.style.display = 'none'
          //   link.href = e.target.result
          //   link.download = fileName
          //   var body = document.querySelector('body')
          //   body.appendChild(link)
          //   link.click()
          //   body.removeChild(link)
          //   window.URL.revokeObjectURL(link.href)
          // }
        })
      },
    },
  }
</script>

<style lang="less" scoped>
  @import url('@/styles/params.less');

  .application-page-header {
    background-color: white;
    padding: 8px 32px;
    font-size: 16px;
    color: #333;
    line-height: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .application-date {
      font-size: 16px;
      color: #333333;
      line-height: 24px;
      text-align: right;
      min-height: 60px;

      &-item {
        margin: 4px 0;
        font-size: 16px;
      }
    }

    .page-title {
      font-size: 20px;
      font-weight: bold;
      color: #333333;
      line-height: 28px;
    }
  }

  .application-page-container {
    margin: @app-conetent-margin;
    background-color: white;
    padding: 24px;

    section {
      display: flex;
      justify-content: space-between;
    }

    .btn-action-group {
      .btn-upload {
        margin-right: 12px;
      }
    }

    .input-search-panel {
      width: 285px;
    }

    .application-counter {
      padding: 24px 0;
      align-items: center;

      .counter-text {
        font-size: 16px;
        font-weight: bold;
        color: #333333;
        line-height: 24px;
        margin-bottom: 24px;

        span {
          margin-right: 12px;
        }
      }

      .btn-download-assessment {
        margin-right: 12px;
      }

      .btn-export {
        margin-right: 12px;
      }
    }
  }

  .table-pagination {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
  }

  :deep {
    .table-pagination,
    .page-pagination {
      .ant-pagination-item-active {
        a {
          color: white;
        }

        background-color: @primary-color;
      }
    }
  }

  .appliction-page-success {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 5;
    height: 495px;
    background-color: white;
    font-size: 16px;
    color: #333;
    line-height: 24px;

    .msg-icon {
      width: 72px;
      height: 72px;
      color: #43a047;
    }

    .btn-group-success {
      margin-top: 60px;
      display: flex;
      justify-content: center;

      .btn-view,
      .btn-back {
        margin: 0 8px;
      }
    }
  }

  span.highlight {
    color: @primary-color;
    font-weight: bold;
  }

  .download-assessment-buttons {
    display: flex;
    justify-content: space-between;
  }
</style>
